import { useState, useEffect } from 'react';

/* Month / Quarter / Year (and optionally a custom date range) with ‹ › to step back and forward.
   Calls onChange({ from, to, label }) with plain 'YYYY-MM-DD' dates (California calendar).
   Used by the Leaderboard and the Sales Report.                                            */

const TZ = 'America/Los_Angeles';
export const todayCA = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
const pad = n => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();   // m = 1..12
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const shortDate = iso => { const [y, m, d] = iso.split('-').map(Number); return `${MONTHS[m - 1].slice(0, 3)} ${d}, ${y}`; };

// anchor = { y, n } where n is the month (1-12), quarter (1-4) or unused for a year
export function periodRange(mode, y, n) {
  if (mode === 'month') return { from: `${y}-${pad(n)}-01`, to: `${y}-${pad(n)}-${pad(lastDay(y, n))}`, label: `${MONTHS[n - 1]} ${y}` };
  if (mode === 'quarter') { const m1 = (n - 1) * 3 + 1, m3 = m1 + 2; return { from: `${y}-${pad(m1)}-01`, to: `${y}-${pad(m3)}-${pad(lastDay(y, m3))}`, label: `Q${n} ${y} (${MONTHS[m1 - 1].slice(0, 3)}–${MONTHS[m3 - 1].slice(0, 3)})` }; }
  return { from: `${y}-01-01`, to: `${y}-12-31`, label: `Year ${y}` };
}

const CSS = `
.pp{display:flex;flex-wrap:wrap;gap:10px;align-items:center}
.pp-seg{display:inline-flex;border:1px solid #d6dfdb;border-radius:10px;overflow:hidden;background:#fff}
.pp-seg button{border:none;background:#fff;padding:8px 14px;font-weight:700;font-size:13px;cursor:pointer;color:#4A5D55}
.pp-seg button.on{background:#0D3B2E;color:#fff}
.pp-nav{display:inline-flex;align-items:center;gap:6px}
.pp-arrow{width:34px;height:34px;border-radius:9px;border:1px solid #d6dfdb;background:#fff;font-size:17px;cursor:pointer;color:#0D3B2E;line-height:1}
.pp-arrow:hover{background:#EEF3F0}
.pp-arrow:disabled{opacity:.35;cursor:default}
.pp-label{font-weight:800;color:#0D3B2E;font-size:15px;min-width:170px;text-align:center}
.pp-now{border:1px solid #d6dfdb;background:#fff;border-radius:9px;padding:7px 11px;font-size:12px;font-weight:700;color:#0D3B2E;cursor:pointer}
.pp input[type=date]{padding:7px 9px;border:1px solid #d6dfdb;border-radius:9px;font:inherit;font-size:13px}
.pp-quick{border:1px dashed #9BB5A8;background:#fff;border-radius:9px;padding:6px 10px;font-size:12px;font-weight:700;color:#0D3B2E;cursor:pointer}
@media print{.pp{display:none}}
`;

export default function PeriodPicker({ onChange, allowCustom = false, initialMode = 'month' }) {
  const t = todayCA();
  const ty = Number(t.slice(0, 4)), tm = Number(t.slice(5, 7)), tq = Math.ceil(tm / 3);
  const [mode, setMode] = useState(initialMode);
  const [y, setY] = useState(ty);
  const [n, setN] = useState(initialMode === 'quarter' ? tq : tm);
  const [from, setFrom] = useState(`${ty}-01-01`);
  const [to, setTo] = useState(t);

  // switching mode jumps to the period that contains today
  function pick(m) {
    setMode(m); setY(ty);
    if (m === 'month') setN(tm); else if (m === 'quarter') setN(tq);
  }
  function step(dir) {
    if (mode === 'year') { setY(v => v + dir); return; }
    const max = mode === 'month' ? 12 : 4;
    let nn = n + dir, yy = y;
    if (nn < 1) { nn = max; yy -= 1; } else if (nn > max) { nn = 1; yy += 1; }
    setN(nn); setY(yy);
  }
  const isNow = mode === 'year' ? y === ty : y === ty && n === (mode === 'month' ? tm : tq);

  const range = mode === 'custom'
    ? { from: from <= to ? from : to, to: from <= to ? to : from, label: `${shortDate(from <= to ? from : to)} – ${shortDate(from <= to ? to : from)}` }
    : periodRange(mode, y, n);

  useEffect(() => { if (range.from && range.to) onChange(range); }, [range.from, range.to, range.label]); // eslint-disable-line

  const back = days => { const d = new Date(t + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() - days); return d.toISOString().slice(0, 10); };

  return (
    <div className="pp">
      <style>{CSS}</style>
      <div className="pp-seg">
        <button type="button" className={mode === 'month' ? 'on' : ''} onClick={() => pick('month')}>Month</button>
        <button type="button" className={mode === 'quarter' ? 'on' : ''} onClick={() => pick('quarter')}>Quarter</button>
        <button type="button" className={mode === 'year' ? 'on' : ''} onClick={() => pick('year')}>Year</button>
        {allowCustom && <button type="button" className={mode === 'custom' ? 'on' : ''} onClick={() => setMode('custom')}>Custom range</button>}
      </div>
      {mode !== 'custom' ? (
        <div className="pp-nav">
          <button type="button" className="pp-arrow" onClick={() => step(-1)} aria-label="Previous">‹</button>
          <span className="pp-label">{range.label}</span>
          <button type="button" className="pp-arrow" onClick={() => step(1)} aria-label="Next">›</button>
          {!isNow && <button type="button" className="pp-now" onClick={() => pick(mode)}>{mode === 'year' ? 'This year' : mode === 'quarter' ? 'This quarter' : 'This month'}</button>}
        </div>
      ) : (
        <div className="pp-nav" style={{ flexWrap: 'wrap' }}>
          <input type="date" value={from} onChange={e => e.target.value && setFrom(e.target.value)} aria-label="From" />
          <span style={{ color: '#7a8a82', fontWeight: 700 }}>to</span>
          <input type="date" value={to} onChange={e => e.target.value && setTo(e.target.value)} aria-label="To" />
          <button type="button" className="pp-quick" onClick={() => setTo(t)}>to today</button>
          <button type="button" className="pp-quick" onClick={() => { setFrom(back(29)); setTo(t); }}>Last 30 days</button>
          <button type="button" className="pp-quick" onClick={() => { setFrom(back(89)); setTo(t); }}>Last 90 days</button>
        </div>
      )}
    </div>
  );
}
